import React, { useEffect } from 'react';
import { useCartContext } from '../../../utils/cart/context/CartContext';
import { useLocalisationContext } from '../localisation/context/LocalisationContext';
import { IdPrefixProvider } from '@jbhi-fi/components/src/IdPrefix';
import { drawBubbles } from '../bubble/drawBubbles';
import { HeaderLayout } from '@jbhi-fi/components/src/HeaderLayout';
import { useContentfulActiveNavs } from '@jbhi-fi/components/src/hooks/useContentfulActiveNavs/useContentfulActiveNavs';
import { NavTransformer } from '@jbhi-fi/components/src/Contentful/transformers/NavTransformer';
import { useHeaderUtilities } from '@jbhi-fi/components/src/hooks/useHeaderUtilities';
import resolveResponse from 'contentful-resolve-response';
import { ContentfulNav } from '@jbhi-fi/apis/src/contentful/types';
import { MyAccount } from '../myAccount/MyAccount';
import { Portal } from '../../../components/portal/Portal';
import { SearchPortal } from '../../../sections/search';
import { useCouponContext } from '../../../contexts/CouponContextProvider';
import { useCustomerContext } from '@jbhi-fi/components/src/context/CustomerContextProvider';
import { getGapToDate } from '@jbhi-fi/utils/src/date/getGapToDate';
import { handleLoginCreate } from '../../../utils/signInOutUp/AuthenticationHelper';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag/useFeatureFlag';
import { SnackbarProvider, useSnackbarContext } from '../snackbars/context/SnackbarContext';
import { useHeaderPadding } from './useHeaderPadding';
import { useShopifyProduct } from '../../../api/shopify/ShopifyProduct';
import { ShopifyProductTransformer } from '@jbhi-fi/components/src/Shopify/transformers/ShopifyProductTransformer';
import { HeaderNavigationProps } from '@jbhi-fi/components/src/HeaderLayout/components/HeaderNavigation/HeaderNavigation';

export const HEADER_CONTAINER_ID = 'react-root-header';
const PERKS_PAGE_PATH = '/pages/perks';

const shouldUseCachedHeaderFooter = window?.Jbhifi?.contentfulSettings?.shouldUseCachedHeaderFooter ?? true;

const getInitialHeaderNavData = (): ContentfulNav | HeaderNavigationProps | undefined => {
  try {
    const rawDataString = shouldUseCachedHeaderFooter
      ? window?.Jbhifi?.headerNavTransformedDataString
      : window?.Jbhifi?.headerNavDataString;

    if (!rawDataString) return undefined;

    const headerNavRawData = JSON.parse(decodeURIComponent(rawDataString) || '{}');

    if (!shouldUseCachedHeaderFooter) {
      headerNavRawData.includes = headerNavRawData.includes || {};
      headerNavRawData.includes.asset = headerNavRawData.includes.asset || [];
      const headerNavData = resolveResponse(headerNavRawData);
      return headerNavData?.[0] as ContentfulNav;
    }

    return headerNavRawData as HeaderNavigationProps;
  } catch (err) {
    console.error('Error parsing header nav data', err);
    return undefined;
  }
};

const HeaderLocalised = () => {
  useHeaderPadding();
  const { shopify, productMetafields, marketplaceSellers } = useShopifyProduct();

  const { Product, Department, ProductGroup } = productMetafields;
  const hasPreOrderTag: boolean = Product?.Flags?.some(flag => flag.Name === 'Pre Order');
  const isPOreOrderClickAndCollect: boolean = Product?.Flags?.some(flag => flag.Name === 'Pre Order Click & Collect');
  const isPreOrder: boolean = hasPreOrderTag || isPOreOrderClickAndCollect;
  const sku = shopify?.variants[0]?.sku ? parseInt(shopify?.variants[0]?.sku) : 0;
  const isMarketplaceProduct =
    ShopifyProductTransformer.isMarketplaceProduct({ product: shopify }) && !!marketplaceSellers?.[0];
  const {
    isCouponSurfacingEnabled,
    newCouponCount,
    coupons,
    couponOffering,
    shouldShowSpendAndGetBanner,
    currentSpend,
    isProductExcluded,
    handleSelectCoupon,
    handleUpdateShowSpendAndGetBanner,
  } = useCouponContext({
    location: 'header',
    product: {
      departmentCode: Department?.Id?.toString(),
      sku: sku.toString(),
      productGroupId: ProductGroup?.Id?.toString() || '',
      supplierNumber: Product?.SupplierProductDetails?.[0]?.SupplierNumber.toString(),
      isPreOrder: isPreOrder,
      isMarketplace: isMarketplaceProduct,
    },
  });

  const isPerksPage = window.location.pathname === PERKS_PAGE_PATH;
  const { isPerks, customerPreferences } = useCustomerContext();
  const { birthdayMonth } = useCustomerContext();
  const daysToBirthday = getGapToDate({ measure: 'days', month: birthdayMonth });
  const isCloseToBirthMonth = daysToBirthday > 0 && daysToBirthday <= 21;
  const isPerksSignupEnabled = useFeatureFlag('isHeaderPerksSignupEnabled');
  const isPerksBirthdayFormEnabled = useFeatureFlag('isHeaderPerksBirthdayFormEnabled');

  const { isLocalisationEnabled } = useLocalisationContext();
  const { cart, toggleMiniCart, location } = useCartContext();
  const offering =
    !isPerksPage && couponOffering
      ? {
          coupon: couponOffering,
          currentSpend,
          shouldShowSpendAndGetBanner,
          isProductExcluded,
          handleSelectCoupon,
          handleUpdateShowSpendAndGetBanner,
        }
      : undefined;

  const shouldShowBirthdayField =
    isPerksBirthdayFormEnabled &&
    isPerks &&
    !birthdayMonth &&
    customerPreferences?.shopify_perks_skipSetBirthday !== 'true';
  const shouldShowPerksSignup =
    !isPerks && isPerksSignupEnabled && customerPreferences?.perks_skipJoinPrompt !== 'true';

  const headerUtilities = useHeaderUtilities({
    totalCartQuantity: cart?.item_count ?? 0,
    newCouponCount: isCouponSurfacingEnabled ? newCouponCount : undefined,
    coupons: coupons,
    isPerks: isPerks,
    isCloseToBirthMonth: isCloseToBirthMonth,
    shouldShowPerksSignup: shouldShowPerksSignup,
    shouldShowBirthdayField: shouldShowBirthdayField,
    MyAccountSubMenuComponent: MyAccount,
    onLoginClick: () => {
      handleLoginCreate(window.location.href);
    },
    onCartClick: () => location !== 'cart' && toggleMiniCart(true),
  });

  const isCartPage = window.location.pathname === '/cart';
  const { headerNav } = useContentfulActiveNavs({});
  const initialHeaderNav = getInitialHeaderNavData();
  const { snackbarProps } = useSnackbarContext();

  const navData = headerNav || initialHeaderNav;
  const headerNavProps =
    navData && !shouldUseCachedHeaderFooter
      ? NavTransformer.toHeaderNavigationProps(navData as ContentfulNav)
      : (navData as HeaderNavigationProps | undefined);

  return (
    <>
      <HeaderLayout
        utilities={headerUtilities}
        headerNavigationProps={headerNavProps}
        isLocalisationEnabled={isLocalisationEnabled}
        snackbarProps={snackbarProps}
        logoSubtitle={window.Jbhifi.logoSubtitle}
        couponOffering={offering}
        isCartPage={isCartPage}
        searchPlaceholder={window.Jbhifi?.searchPlaceholder}
        searchTooltipConfig={window.Jbhifi?.searchTooltipConfig}
      />
    </>
  );
};

export const HeaderPortal = () => {
  const container = window.document.getElementById(HEADER_CONTAINER_ID);

  useEffect(() => {
    drawBubbles();
  }, []);

  if (!container) return null;

  return (
    <>
      <Portal container={container}>
        <IdPrefixProvider prefix="header">
          <SnackbarProvider>
            <HeaderLocalised />
          </SnackbarProvider>
        </IdPrefixProvider>
      </Portal>
      <SearchPortal />
    </>
  );
};
